import { useMutation, UseMutationResult } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import 'react-toastify/dist/ReactToastify.css';
import { useTranslation } from 'react-i18next';
import { API_URLS } from '../constants/apiUrl';
import { useLoading } from '../context/LoadingContext';
import { post } from '../api/api';
import { showToast } from '../components/utils/toast';
import { IMatterRequest, IMatterRequestParams } from '../interface/matterRequest';
import { useAnalytics } from './useAnalytics';
import { AnalyticsFeatureOperation } from '../interface/analytics';
import {
  getMatterRequestCreateErrorMessage,
  isPrincipalCustomerInactiveError,
} from '../helpers/matterRequestErrorHelper';
import { useMatterRequestMutationErrorHandler } from './useMatterRequestMutationErrorHandler';

const createMatterRequest = async (
  matterRequestData: IMatterRequestParams,
): Promise<IMatterRequest> => {
  return post(`${API_URLS.CREATE_MATTER_REQUEST}`, matterRequestData, {
    skipGlobalErrorHandler: true,
    skipForbiddenRedirect: true,
  });
};

interface UseCreateMatterRequestOptions {
  /** Takes over the inactive customer failure from the error toast, for forms that show it on the customer field. */
  onPrincipalCustomerInactive?: (matterRequestData: IMatterRequestParams) => void;
}

export const useCreateMatterRequest = ({
  onPrincipalCustomerInactive,
}: UseCreateMatterRequestOptions = {}): UseMutationResult<
  IMatterRequest,
  AxiosError<{ message?: string; type?: string }>,
  { matterRequestData: IMatterRequestParams }
> => {
  const { setIsLoading } = useLoading();
  const { t } = useTranslation();
  const { trackBusinessEvent } = useAnalytics();
  const handleError = useMatterRequestMutationErrorHandler((error) =>
    getMatterRequestCreateErrorMessage(error, t),
  );

  return useMutation({
    mutationFn: ({ matterRequestData }) => createMatterRequest(matterRequestData),
    onMutate: () => {
      setIsLoading(true);
    },
    onSuccess: () => {
      trackBusinessEvent(AnalyticsFeatureOperation.MATTER_REQUEST_CREATION);
      showToast('success', t('matterRequest:matterRequestCreated'));
    },
    onError: (error, { matterRequestData }) => {
      if (onPrincipalCustomerInactive && isPrincipalCustomerInactiveError(error)) {
        onPrincipalCustomerInactive(matterRequestData);
        return;
      }
      handleError(error);
    },
    onSettled: () => {
      setIsLoading(false);
    },
  });
};
